import { StateCreator } from 'zustand';
import { AssetType, HighwaysCarriagewaysFeatureAttributes } from '@types';

type WasteSubcategory = {
  name: string;
  parentCategory: string;
  parentCategoryLabel: string;
};
export type WasteLocation = {
  address_line_1: string;
  address_line_2: string;
  town_city: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
};

type Question = Array<{
  key: string;
  name: string;
  parentSubcategories: string[];
  fieldType: string;
  response: string;
}>;

export type CaseIDProps = {
  case_reference: string;
  sla_text: string;
};

interface WasteCategoryQuestionsAndOptionsProps {
  response: string;
  parentQuestion: string;
  options: string[];
}

export const emptyServiceAssetDetails: HighwaysCarriagewaysFeatureAttributes = {
  site_class_code: '',
  site_class_name: '',
  central_asset_id: '',
  contract_area_code: '',
  feature_type_code: '',
  layer: '' as AssetType,
};
// fill in mock data for testing -> to remove in production

export const emptyLocation = {
  address_line_1: '',
  address_line_2: '',
  town_city: '',
  county: '',
  postcode: '',
  country: '',
  easting: '',
  northing: '',
  UPRN: '',
  USRN: '',
  latitude: '',
  longitude: '',
  officialAddress: '',
};

export interface WasteDetailsSlice {
  wasteCategory: string;
  isImageMismatch: boolean;
  currentSubcategory: WasteSubcategory[];
  renderedQuestions: Question | undefined;
  renderedWasteCategoryQuestionsAndOptions:
    | WasteCategoryQuestionsAndOptionsProps[]
    | undefined;
  locationOfWaste: WasteLocation;
  canYouGiveMoreDetail: string;
  anythingElseWeNeedToKnow: string;
  receiveConfirmationEmail: string;
  surfaceDetails: string;
  actualMeasurementDetails: string;
  offensive: boolean;
  isRecurringIssue: boolean | undefined;
  whenRecurringHappened: string | undefined;
  confirmation: CaseIDProps | undefined;
  selectedOptionRef: string;
  selectedSubcategory: string;
  isNavigate: boolean;
  updateWasteCategory: (wasteCategory: string) => void;
  updateIsNavigate: (isNavigate: boolean) => void;
  updateCurrentSubcategory: (currentSubcategory: WasteSubcategory[]) => void;
  updateRenderedQuestions: (renderedQuestions: Question | undefined) => void;
  updateRenderedWasteCategoryQuestionsAndOptions: (
    renderedWasteCategoryQuestionsAndOptions:
      | WasteCategoryQuestionsAndOptionsProps[]
      | undefined,
  ) => void;
  updateConfirmation: (case_id: CaseIDProps | undefined) => void;
  updateLocationOfWaste: (locationOfWaste: WasteLocation) => void;
  updateCanYouGiveMoreDetail: (moreDetailOnWaste: string) => void;
  updateAnythingElseWeNeedToKnow: (anythingElseWeNeedToKnow: string) => void;
  updateReceiveConfirmationEmail: (receiveConfirmationEmail: string) => void;
  updateSurfacedetails: (surfaceDetails: string) => void;
  updateActualMeasurementDetails: (actualMeasurementDetails: string) => void;
  updateOffensive: (offensive: boolean) => void;
  updateIsImageMismatch: (isImageMismatch: boolean) => void;
  resetWasteDetails: () => void;
  updateSelectedOptionRef: (selectedOptionRef: string) => void;
  updateSelectedSubcategory: (selectedSubcategory: string) => void;
  resetAllWasteDetails: () => void;
  slaText: string;
  updateSlaText: (slaText: string | undefined) => void;
  updateIsRecurringIssue: (isRecurringIssue: boolean | undefined) => void;
  updateWhenRecurringHappened: (
    whenRecurringHappened: string | undefined,
  ) => void;
}

export const createWasteDetailsSlice: StateCreator<
  WasteDetailsSlice,
  [],
  [],
  WasteDetailsSlice
> = (set) => ({
  wasteCategory: '',
  currentSubcategory: [
    {
      name: '',
      parentCategory: '',
      parentCategoryLabel: '',
    },
  ],
  locationOfWaste: emptyLocation,
  canYouGiveMoreDetail: '',
  anythingElseWeNeedToKnow: '',
  receiveConfirmationEmail: '',
  isImageMismatch: false,
  renderedQuestions: undefined,
  surfaceDetails: '',
  actualMeasurementDetails: '',
  offensive: false,
  isRecurringIssue: undefined,
  whenRecurringHappened: undefined,
  confirmation: undefined,
  selectedOptionRef: '',
  selectedSubcategory: '',
  isNavigate: false,
  renderedWasteCategoryQuestionsAndOptions: undefined,
  updateWasteCategory: (wasteCategory) => set(() => ({ wasteCategory })),
  updateIsNavigate: (isNavigate) => set(() => ({ isNavigate })),
  updateCurrentSubcategory: (currentSubcategory: WasteSubcategory[]) =>
    set(() => ({ currentSubcategory })),
  updateRenderedQuestions: (renderedQuestions: Question | undefined) =>
    set(() => ({ renderedQuestions })),

  updateRenderedWasteCategoryQuestionsAndOptions: (
    renderedWasteCategoryQuestionsAndOptions:
      | WasteCategoryQuestionsAndOptionsProps[]
      | undefined,
  ) => set(() => ({ renderedWasteCategoryQuestionsAndOptions })),
  updateConfirmation: (confirmation: CaseIDProps | undefined) =>
    set(() => ({ confirmation })),
  updateLocationOfWaste: (locationOfWaste) => set(() => ({ locationOfWaste })),
  updateCanYouGiveMoreDetail: (canYouGiveMoreDetail) =>
    set(() => ({ canYouGiveMoreDetail })),
  updateAnythingElseWeNeedToKnow: (anythingElseWeNeedToKnow) =>
    set(() => ({ anythingElseWeNeedToKnow })),
  updateReceiveConfirmationEmail: (receiveConfirmationEmail) =>
    set(() => ({ receiveConfirmationEmail })),
  updateSurfacedetails: (surfaceDetails) => set(() => ({ surfaceDetails })),
  updateOffensive: (offensive) => set(() => ({ offensive })),
  updateSelectedSubcategory: (selectedSubcategory) =>
    set(() => ({ selectedSubcategory })),
  updateActualMeasurementDetails: (actualMeasurementDetails) =>
    set(() => ({ actualMeasurementDetails })),
  updateIsImageMismatch: (isImageMismatch) => set(() => ({ isImageMismatch })),
  updateSelectedOptionRef: (selectedOptionRef) =>
    set(() => ({ selectedOptionRef })),
  slaText: '',
  updateSlaText: (slaText) => set(() => ({ slaText })),
  updateIsRecurringIssue: (isRecurringIssue) =>
    set(() => ({ isRecurringIssue })),
  updateWhenRecurringHappened: (whenRecurringHappened) =>
    set(() => ({ whenRecurringHappened })),
  resetWasteDetails: () =>
    set(() => ({
      currentSubcategory: [
        {
          name: '',
          parentCategory: '',
          parentCategoryLabel: '',
        },
      ],
      canYouGiveMoreDetail: '',
      locationOfWaste: emptyLocation,
      anythingElseWeNeedToKnow: '',
      receiveConfirmationEmail: '',
      renderedQuestions: undefined,
      renderedWasteCategoryQuestionsAndOptions: undefined,
      surfaceDetails: '',
      actualMeasurementDetails: '',
      offensive: false,
      selectedOptionRef: '',
      confirmation: undefined,
      isRecurringIssue: undefined,
      whenRecurringHappened: undefined,
      isImageMismatch: false,
      isNavigate: false,
    })),
  resetAllWasteDetails: () =>
    set(() => ({
      currentSubcategory: [
        {
          name: '',
          parentCategory: '',
          parentCategoryLabel: '',
        },
      ],
      locationOfWaste: emptyLocation,
      canYouGiveMoreDetail: '',
      anythingElseWeNeedToKnow: '',
      receiveConfirmationEmail: '',
      isImageMismatch: false,
      renderedQuestions: undefined,
      surfaceDetails: '',
      actualMeasurementDetails: '',
      offensive: false,
      isRecurringIssue: undefined,
      whenRecurringHappened: undefined,
    })),
});
